import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { buildCorrectedTextSegments, buildPlainCorrectedText } from "./utils";

interface AnswerComparisonTabsProps {
  readonly answerId: string;
  readonly correctedVersion: string;
  readonly expectedAnswers: readonly string[];
  readonly yourAnswer: ReactNode;
  readonly activeAnnotationId?: string | null;
}

type TabValue = "your-answer" | "corrected" | "clean" | "expected";

export function AnswerComparisonTabs({
  answerId,
  correctedVersion,
  expectedAnswers,
  yourAnswer,
  activeAnnotationId,
}: AnswerComparisonTabsProps) {
  const { t } = useTranslation();
  const hasCorrected = correctedVersion.trim().length > 0;
  const plainCorrected = hasCorrected
    ? buildPlainCorrectedText(correctedVersion)
    : "";

  const visibleTabs = useMemo<TabValue[]>(() => {
    const list: TabValue[] = ["your-answer"];
    if (hasCorrected) {
      list.push("corrected", "clean");
    }
    list.push("expected");
    return list;
  }, [hasCorrected]);

  const defaultTab: TabValue = hasCorrected ? "corrected" : "your-answer";
  const [tab, setTab] = useState<TabValue>(defaultTab);

  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(defaultTab);
    }
  }, [tab, visibleTabs, defaultTab]);

  useEffect(() => {
    if (!activeAnnotationId) return;
    if (activeAnnotationId.startsWith(`answer-${answerId}-`)) {
      setTab("your-answer");
    }
  }, [activeAnnotationId, answerId]);

  const triggerClass =
    "font-ui min-w-[8rem] flex-1 snap-start text-xs lg:min-w-0";

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value as TabValue)}
      className="flex flex-col"
      data-answer-comparison-tabs
    >
      <TabsList
        className={cn(
          "scrollbar-hide flex w-full snap-x snap-mandatory justify-start overflow-x-auto bg-muted/60 lg:grid lg:overflow-visible",
          visibleTabs.length === 2 && "lg:grid-cols-2",
          visibleTabs.length === 3 && "lg:grid-cols-3",
          visibleTabs.length === 4 && "lg:grid-cols-4",
        )}
      >
        <TabsTrigger value="your-answer" className={triggerClass}>
          {t("simulation.results.reviewDialog.tabs.yourAnswerWithErrors", {
            defaultValue: "Your Answer With Errors",
          })}
        </TabsTrigger>
        {hasCorrected && (
          <TabsTrigger value="corrected" className={triggerClass}>
            {t("simulation.results.reviewDialog.tabs.correctedVersion", {
              defaultValue: "Corrected Version",
            })}
          </TabsTrigger>
        )}
        {hasCorrected && (
          <TabsTrigger value="clean" className={triggerClass}>
            {t("simulation.results.reviewDialog.tabs.cleanCorrection", {
              defaultValue: "Clean Correction",
            })}
          </TabsTrigger>
        )}
        <TabsTrigger value="expected" className={triggerClass}>
          {t("simulation.results.reviewDialog.expectedAnswer", {
            defaultValue: "Expected Answer",
          })}
        </TabsTrigger>
      </TabsList>

      <TabsContent
        value="your-answer"
        className="mt-3 data-[state=inactive]:hidden"
        forceMount
      >
        <div className="flex flex-col space-y-2">
          <Label className="font-ui text-xs font-medium text-primary uppercase tracking-widest">
            {t("simulation.results.reviewDialog.yourAnswer", {
              defaultValue: "Your Answer",
            })}
          </Label>
          {yourAnswer}
        </div>
      </TabsContent>

      {hasCorrected && (
        <TabsContent
          value="corrected"
          className="mt-3 data-[state=inactive]:hidden"
          forceMount
        >
          <div className="flex flex-col space-y-2" data-corrected-answer-panel>
            <Label className="font-ui text-xs font-medium text-success uppercase tracking-widest">
              {t("simulation.results.reviewDialog.correctedVersion", {
                defaultValue: "Corrected Version",
              })}
            </Label>
            <div className="font-ui flex-1 rounded-md border border-success/20 bg-success/5 p-3 text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap selection:bg-success/20">
              {buildCorrectedTextSegments(correctedVersion)}
            </div>
          </div>
        </TabsContent>
      )}

      {hasCorrected && (
        <TabsContent
          value="clean"
          className="mt-3 data-[state=inactive]:hidden"
          forceMount
        >
          <div className="flex flex-col space-y-2">
            <Label className="font-ui text-xs font-medium text-success uppercase tracking-widest">
              {t("simulation.results.reviewDialog.tabs.cleanCorrection", {
                defaultValue: "Clean Correction",
              })}
            </Label>
            <div className="font-ui flex-1 rounded-md border border-success/20 bg-success/5 p-3 text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap selection:bg-success/20">
              {plainCorrected}
            </div>
          </div>
        </TabsContent>
      )}

      <TabsContent
        value="expected"
        className="mt-3 data-[state=inactive]:hidden"
        forceMount
      >
        <div className="flex flex-col space-y-2">
          <Label className="font-ui text-xs font-medium text-success uppercase tracking-widest">
            {t("simulation.results.reviewDialog.expectedAnswer", {
              defaultValue: "Expected Answer",
            })}
          </Label>
          {expectedAnswers.length > 0 ? (
            <div className="font-ui flex-1 rounded-md border border-success/20 bg-success/5 p-3 text-sm text-foreground/90 whitespace-pre-wrap selection:bg-success/20">
              {expectedAnswers.map((expected, idx) => (
                <div
                  key={`${answerId}-correct-${idx}`}
                  className="flex items-start gap-2"
                >
                  {expectedAnswers.length > 1 && (
                    <span className="mt-1 opacity-50">•</span>
                  )}
                  <span>{expected}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="font-ui flex-1 rounded-md bg-muted/30 p-3 text-center text-sm italic text-muted-foreground">
              {t("simulation.results.reviewDialog.noExpectedAnswer", {
                defaultValue: "No specific expected answer available.",
              })}
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
